"use client";

import React, { useState, useEffect } from "react";
import { useAuth } from "./Auth";
import { 
  Home, 
  Menu, 
  X, 
  Settings, 
  LogOut, 
  User, 
  ChevronDown,
  ChevronRight,
  Palette,
  FileText,
  Video,
  Brain,
  Code,
  Languages,
  TrendingUp,
  Target,
  Mail,
  BarChart3,
  Globe,
  Shield,
  Zap,
  Sparkles
} from "lucide-react";

interface NavigationProps {
  className?: string;
}

export default function ModernNavigation({ className = "" }: NavigationProps) {
  const { user, logout, isAdmin } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isToolsDropdownOpen, setIsToolsDropdownOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);

  // Fechar menu ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.mobile-menu-container') && !target.closest('.mobile-menu-button')) {
        setIsMobileMenuOpen(false);
      }
      if (!target.closest('.tools-dropdown')) {
        setIsToolsDropdownOpen(false);
      }
    };

    if (isMobileMenuOpen || isToolsDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen, isToolsDropdownOpen]);

  const navigationItems = [
    { name: "Início", href: "/", icon: Home },
  ];

  const toolsCategories = [
    {
      category: "Criação de Conteúdo",
      id: "criacao-conteudo",
      icon: Palette,
      color: "from-blue-500 to-cyan-500",
      items: [
        { name: "Gerar Imagem", href: "/admin", icon: Palette, tab: "image" },
        { name: "Gerar Pitch", href: "/admin", icon: FileText, tab: "pitch" },
        { name: "Script de Vídeo", href: "/admin", icon: Video, tab: "video" },
        { name: "Conteúdo Viral", href: "/admin", icon: TrendingUp, tab: "viral" },
        { name: "Sugestões IA", href: "/admin", icon: Sparkles, tab: "prompts" }
      ]
    },
    {
      category: "Marketing & Vendas",
      id: "marketing-vendas",
      icon: Target,
      color: "from-green-500 to-emerald-500",
      items: [
        { name: "Black Friday", href: "/admin", icon: Target, tab: "black-friday" },
        { name: "Email Sequence", href: "/admin", icon: Mail, tab: "email" },
        { name: "Funil de Vendas", href: "/admin", icon: BarChart3, tab: "funnel" },
        { name: "Landing Page", href: "/admin", icon: Globe, tab: "landing" },
        { name: "Campanha Ads", href: "/admin", icon: Zap, tab: "ads" }
      ]
    },
    {
      category: "Análise & Inteligência",
      id: "analise-inteligencia",
      icon: Brain,
      color: "from-purple-500 to-pink-500",
      items: [
        { name: "Análise de Sentimento", href: "/admin", icon: Brain, tab: "sentiment" },
        { name: "OSINT", href: "/admin", icon: Shield, tab: "osint" },
        { name: "Análise de Vendas", href: "/admin", icon: BarChart3, tab: "analytics" }
      ]
    },
    {
      category: "Ferramentas Técnicas",
      id: "ferramentas-tecnicas",
      icon: Code,
      color: "from-yellow-500 to-amber-500",
      items: [
        { name: "Gerar Código", href: "/admin", icon: Code, tab: "code" },
        { name: "Tradutor", href: "/admin", icon: Languages, tab: "translate" },
        { name: "Resumidor", href: "/admin", icon: FileText, tab: "summarize" }
      ]
    }
  ];

  const handleToolClick = (tab: string) => {
    localStorage.setItem('selectedTab', tab);
    setIsMobileMenuOpen(false);
    setIsToolsDropdownOpen(false);
  };

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  return (
    <>
      <nav className={`bg-white/95 backdrop-blur-md shadow-lg sticky top-0 z-50 ${className}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center">
              <div className="flex items-center">
                <Sparkles className="w-8 h-8 text-purple-600 mr-2" />
                <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                  Axion IA
                </span>
              </div>
              <div className="hidden sm:flex items-center ml-3">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="ml-2 text-xs sm:text-sm text-gray-600 font-medium">Online</span>
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-1">
              {navigationItems.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  className="flex items-center px-4 py-2 rounded-lg text-sm font-medium text-gray-700 hover:text-purple-600 hover:bg-purple-50 transition-all duration-200"
                >
                  <item.icon className="w-4 h-4 mr-2" />
                  {item.name}
                </a>
              ))}
              
              {/* Tools Dropdown */}
              {user && (
                <div className="relative tools-dropdown">
                  <button
                    onClick={() => setIsToolsDropdownOpen(!isToolsDropdownOpen)}
                    className="flex items-center px-4 py-2 rounded-lg text-sm font-medium text-gray-700 hover:text-purple-600 hover:bg-purple-50 transition-all duration-200"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Ferramentas
                    <ChevronDown className={`ml-1 w-4 h-4 transition-transform duration-200 ${isToolsDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {isToolsDropdownOpen && (
                    <div className="absolute top-full right-0 mt-2 w-96 bg-white rounded-xl shadow-2xl border border-gray-200 py-2 z-50">
                      {toolsCategories.map((category, catIndex) => (
                        <div key={catIndex} className="border-b border-gray-100 last:border-b-0">
                          <div className="px-4 py-3 flex items-center">
                            <div className={`w-8 h-8 rounded-lg bg-gradient-to-r ${category.color} flex items-center justify-center mr-3`}>
                              <category.icon className="w-4 h-4 text-white" />
                            </div>
                            <span className="text-sm font-semibold text-gray-900">{category.category}</span>
                          </div>
                          <div className="pb-2">
                            {category.items.map((item, index) => (
                              <a
                                key={index}
                                href={item.href}
                                onClick={() => handleToolClick(item.tab)}
                                className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-600 transition-all duration-200"
                              >
                                <item.icon className="w-4 h-4 mr-3 text-gray-500" />
                                {item.name}
                              </a>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {user && (
                <div className="flex items-center space-x-2 ml-4 pl-4 border-l border-gray-200">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-white" />
                    </div>
                    <span className="ml-2 text-sm text-gray-700 font-medium">{user.name}</span>
                  </div>
                  <button
                    onClick={logout}
                    className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200"
                    title="Sair"
                  >
                    <LogOut className="w-4 h-4" />
                  </button>
                </div>
              )}
              
              {!user && (
                <a
                  href="/login"
                  className="px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all duration-200 font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  Entrar
                </a>
              )}
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="mobile-menu-button p-2 rounded-lg text-gray-700 hover:text-purple-600 hover:bg-purple-50 transition-all duration-200"
              >
                <span className="sr-only">Open main menu</span>
                {!isMobileMenuOpen ? (
                  <Menu className="w-6 h-6" />
                ) : (
                  <X className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Side Menu */}
      <div className={`mobile-menu-container fixed inset-0 z-50 md:hidden ${isMobileMenuOpen ? 'block' : 'hidden'}`}>
        {/* Overlay */}
        <div 
          className={`fixed inset-0 bg-black transition-opacity duration-300 ease-out ${
            isMobileMenuOpen ? 'bg-opacity-50' : 'bg-opacity-0'
          }`}
          onClick={() => setIsMobileMenuOpen(false)}
        />
        
        {/* Side Panel */}
        <div className={`fixed top-0 left-0 h-full w-80 bg-white shadow-2xl transform transition-transform duration-300 ease-out ${
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}>
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <Sparkles className="w-8 h-8 mr-2" />
                <span className="text-xl font-bold">Axion IA</span>
              </div>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2 rounded-lg hover:bg-white/20 transition-colors duration-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {user && (
              <div className="flex items-center">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <User className="w-6 h-6" />
                </div>
                <div className="ml-3">
                  <div className="font-semibold text-lg">{user.name}</div>
                  <div className="text-sm opacity-90">Administrador</div>
                </div>
              </div>
            )}
          </div>

          {/* Navigation Items */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-4 space-y-2">
              {navigationItems.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center px-4 py-3 text-base font-medium text-gray-700 hover:text-purple-600 hover:bg-purple-50 rounded-xl transition-all duration-200"
                >
                  <item.icon className="w-5 h-5 mr-3 text-gray-500" />
                  {item.name}
                </a>
              ))}
              
              {user && (
                <>
                  <div className="border-t border-gray-200 pt-4 mt-4">
                    <div className="px-4 py-2 text-sm font-semibold text-gray-500 uppercase tracking-wider">
                      Ferramentas
                    </div>
                    
                    {toolsCategories.map((category) => (
                      <div key={category.id} className="mt-3">
                        <button
                          onClick={() => toggleCategory(category.id)}
                          className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-gray-700 hover:text-purple-600 hover:bg-purple-50 rounded-xl transition-all duration-200"
                        >
                          <div className="flex items-center">
                            <div className={`w-6 h-6 rounded-lg bg-gradient-to-r ${category.color} flex items-center justify-center mr-3`}>
                              <category.icon className="w-3 h-3 text-white" />
                            </div>
                            <span>{category.category}</span>
                          </div>
                          <ChevronRight className={`w-4 h-4 transition-transform duration-200 text-gray-400 ${
                            expandedCategories.includes(category.id) ? 'rotate-90' : ''
                          }`} />
                        </button>
                        
                        <div className={`overflow-hidden transition-all duration-300 ease-out ${
                          expandedCategories.includes(category.id) ? 'max-h-96' : 'max-h-0'
                        }`}>
                          <div className="mt-1 space-y-1">
                            {category.items.map((item, index) => (
                              <a
                                key={index}
                                href={item.href}
                                onClick={() => handleToolClick(item.tab)}
                                className="flex items-center px-8 py-2 text-sm text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-all duration-200"
                              >
                                <item.icon className="w-4 h-4 mr-3 text-gray-500" />
                                {item.name}
                              </a>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="border-t border-gray-200 pt-4 mt-4">
                    <button
                      onClick={() => {
                        logout();
                        setIsMobileMenuOpen(false);
                      }}
                      className="w-full flex items-center px-4 py-3 text-base font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-xl transition-all duration-200"
                    >
                      <LogOut className="w-5 h-5 mr-3" />
                      Sair
                    </button>
                  </div>
                </>
              )}
              
              {!user && (
                <div className="border-t border-gray-200 pt-4 mt-4">
                  <a
                    href="/login"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block w-full px-4 py-3 text-base font-medium text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-xl transition-all duration-200 text-center"
                  >
                    Entrar
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 p-4">
            <div className="text-center text-sm text-gray-500">
              <div className="flex items-center justify-center mb-2">
                <Sparkles className="w-4 h-4 mr-2 text-purple-500" />
                <span className="font-medium">Axion IA Panel</span>
              </div>
              <div className="text-xs">© 2026 Todos os direitos reservados</div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
